import { Box } from '@mui/material';
import { AppConfig } from 'AppConfig';
import * as Constant from 'common/Constant';
import Carousel from 'common/bstCarousel';
import * as UserTypes from 'features/user/userTypes';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { store } from 'store/store';
import * as CustomActions from './actions/action';
import * as ExclusiveActions from '../exclusiveReport/actions/actions';
import ReportFilters from './reportFilters';
import NoAvailableReports from 'features/frontoffice/component/noAvailableReports';
import { sortReports } from 'utils/reportSort';
import { hasPermission } from 'utils/common';
import ReportSectionLoading from '../component/reportSectionLoading';
import { getFirstLevelProjects, getFirstLevelIdSet, resolveDisplayableFirstLevelId, createRootProjectMatcher } from './projectHierarchy';
import { EXCLUSIVE_FILTER_ID, UNCATEGORIZED_FILTER_ID } from './reportFilterConstants';

const CustomReport = ({ selectedFilterTags: selectedFilterTagsProp = [], isTablet = false, searchTerm = '', isMobile = false, sortBy, viewMode = 'grid' }) => {
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const permissions = useSelector((state) => state.user.permissions);
  const customs = useSelector((state) => state.fo.customs);
  const exclusive = useSelector((state) => state.fo.exclusive);
  const exclusiveLoading = useSelector((state) => state.fo.exclusiveLoading);
  const customsLoading = useSelector((state) => state.fo.customsLoading);
  const workbooks = useSelector((state) => state.customReport.workbooks);
  const projects = useSelector((state) => state.customReport.projects);
  const workbooksLoading = useSelector((state) => state.customReport.workbooksLoading);
  const projectsLoading = useSelector((state) => state.customReport.projectsLoading);
  const canViewExclusive = hasPermission(AppConfig.PERMISSION.FO_EXCLUSIVE_REPORTS, permissions);
  const firstLevelProjects = useMemo(() => getFirstLevelProjects(projects), [projects]);
  const firstLevelIdSet = useMemo(() => getFirstLevelIdSet(projects), [projects]);
  const sortKey = typeof sortBy === 'string' ? sortBy : sortBy?.value;

  const [selectedFilterTags, setSelectedFilterTags] = useState([]);
  const selectedMobileFilterTags = Array.isArray(selectedFilterTagsProp) ? selectedFilterTagsProp : [];

  useEffect(() => {
    store.dispatch({
      type: UserTypes.APP_SWITCHED,
      payload: AppConfig.HEADER_MENU_TYPE.FO
    });
    store.dispatch(CustomActions.fetchCustomReports());
    store.dispatch(CustomActions.fetchCustomReportProjects(currentTenantCd));
    store.dispatch(CustomActions.fetchCustomReportWorkbooks(currentTenantCd));
    store.dispatch(CustomActions.setCurrentMenuItem({ name: Constant.ALL, key: Constant.ALL }));
  }, [currentTenantCd]);

  useEffect(() => {
    if (canViewExclusive) {
      store.dispatch(ExclusiveActions.fetchExclusiveReports());
    }
  }, [currentTenantCd, canViewExclusive]);

  const searchedCustomReports = useMemo(() => {
    let list = Array.isArray(customs) ? _.uniqBy(customs, 'reptSource') : [];
    if (sortKey === 'recently_saved') list = list.filter((r) => r.favorited === true);
    list = sortReports(list, sortKey);
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return list;
    return list.filter((item) => {
      const title = (item?.displayText || '').toLowerCase();
      const desc = (item?.description || '').toLowerCase();
      return title.includes(term) || desc.includes(term);
    });
  }, [customs, sortKey, searchTerm]);

  const searchedExclusiveReports = useMemo(() => {
    if (!canViewExclusive) return [];
    let list = Array.isArray(exclusive) ? _.uniqBy(exclusive, 'reptSource') : [];
    list = sortReports(list, sortKey);
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return list;
    return list.filter((item) => {
      const title = (item?.displayText || '').toLowerCase();
      const desc = (item?.description || '').toLowerCase();
      return title.includes(term) || desc.includes(term);
    });
  }, [exclusive, canViewExclusive, sortKey, searchTerm]);

  const reptSourceToProjectId = useMemo(() => {
    const map = new Map();
    (Array.isArray(workbooks) ? workbooks : []).forEach((wb) => {
      if (wb?.reptSource == null) return;
      const firstLevelId = resolveDisplayableFirstLevelId(firstLevelIdSet, wb.projectId);
      if (firstLevelId != null) map.set(String(wb.reptSource), firstLevelId);
    });
    return map;
  }, [workbooks, firstLevelIdSet]);

  const isRootProject = useMemo(() => createRootProjectMatcher(firstLevelProjects), [firstLevelProjects]);

  const rootReptSources = useMemo(() => {
    const set = new Set();
    (Array.isArray(workbooks) ? workbooks : []).forEach((wb) => {
      if (wb?.reptSource == null) return;
      if (isRootProject(wb.projectId)) {
        set.add(String(wb.reptSource));
      }
    });
    return set;
  }, [workbooks, isRootProject]);

  const uncategorizedReptSources = useMemo(() => {
    if (workbooksLoading) return new Set();
    const workbookReptSources = new Set(
      (Array.isArray(workbooks) ? workbooks : []).map((wb) => String(wb?.reptSource))
    );
    return new Set(
      searchedCustomReports
        .map((report) => String(report?.reptSource))
        .filter((reptSource) => !workbookReptSources.has(reptSource))
    );
  }, [searchedCustomReports, workbooks, workbooksLoading]);

  const displayableReports = useMemo(
    () =>
      searchedCustomReports.filter((report) => {
        const reptSource = String(report?.reptSource);
        return reptSourceToProjectId.has(reptSource)
          || rootReptSources.has(reptSource)
          || uncategorizedReptSources.has(reptSource);
      }),
    [searchedCustomReports, reptSourceToProjectId, rootReptSources, uncategorizedReptSources]
  );

  const hasAvailableReports =
    (Array.isArray(customs) && customs.length > 0) || searchedExclusiveReports.length > 0;
  const metaLoading = workbooksLoading || projectsLoading;

  const renderTagSections = (tagsSelected) => {
    const selectedTags = Array.isArray(tagsSelected) ? tagsSelected : [];
    const exclusiveSelected = canViewExclusive && selectedTags.some(
      (tag) => tag?.id === EXCLUSIVE_FILTER_ID
    );
    const uncategorizedSelected = selectedTags.some((tag) => tag?.id === UNCATEGORIZED_FILTER_ID);
    const selectedProjects = selectedTags.filter(
      (tag) => tag?.id !== EXCLUSIVE_FILTER_ID && tag?.id !== UNCATEGORIZED_FILTER_ID
    );
    const isAll = selectedProjects.length === 0 && !exclusiveSelected && !uncategorizedSelected;

    if (isAll && searchedCustomReports.length === 0 && searchedExclusiveReports.length === 0) {
      return <NoAvailableReports />;
    }

    if (
      metaLoading
      && searchedCustomReports.length > 0
      && (isAll || selectedProjects.length > 0 || uncategorizedSelected)
    ) {
      return <ReportSectionLoading count={isMobile ? 6 : 10} variant="grid" />;
    }

    if (isAll) {
      const allReports = sortReports(
        _.uniqBy([...displayableReports, ...searchedExclusiveReports], 'reptSource'),
        sortKey
      );
      if (allReports.length === 0) return <NoAvailableReports />;
      return (
        <Carousel
          items={allReports}
          type={AppConfig.CUSTOM_CAROUSEL}
          isTablet={isTablet}
          viewMode={viewMode}
        />
      );
    }
    const selectedIds = new Set(selectedProjects.map((project) => String(project?.id)));
    const selectedCustomReports = (selectedIds.size > 0 || uncategorizedSelected)
      ? searchedCustomReports.filter((report) => {
        const reptSource = String(report?.reptSource);
        if (
          uncategorizedSelected
          && (rootReptSources.has(reptSource) || uncategorizedReptSources.has(reptSource))
        ) return true;
        const firstLevelId = reptSourceToProjectId.get(reptSource);
        return firstLevelId != null && selectedIds.has(String(firstLevelId));
      })
      : [];
    const selectedReports = sortReports(
      _.uniqBy([
        ...selectedCustomReports,
        ...(exclusiveSelected ? searchedExclusiveReports : [])
      ], 'reptSource'),
      sortKey
    );

    if (selectedReports.length === 0) return <NoAvailableReports />;

    return (
      <Carousel
        items={selectedReports}
        type={AppConfig.CUSTOM_CAROUSEL}
        isTablet={isTablet}
        viewMode={viewMode}
      />
    );
  };

  if (customsLoading || (canViewExclusive && exclusiveLoading)) {
    return <ReportSectionLoading count={isMobile ? 6 : 10} variant="grid" />;
  }
  if (!hasAvailableReports) return <NoAvailableReports />;

  if (isMobile) return <>{renderTagSections(selectedMobileFilterTags)}</>;

  return (
    <>
      {(searchedCustomReports.length > 0 || searchedExclusiveReports.length > 0) && (
        <Box sx={{ mb: 3 }}>
          <ReportFilters isMobile={isMobile} onSelectionChange={setSelectedFilterTags} />
        </Box>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {renderTagSections(selectedFilterTags)}
      </Box>
    </>
  );
};

export default CustomReport;